import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, faCircleXmark, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import * as ToastPrimitive from '@radix-ui/react-toast';
import * as React from 'react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Button from './Button.tsx';
import { withIcon } from './Icon.tsx';
import { TOAST_DURATION } from '../../../constants.ts';
import classnames from '../utils/classnames.tsx';

export interface ToastMessage {
    id?: number; // assigned automatically
    title?: string | React.ReactNode;
    description: string | React.ReactNode;
    status?: 'success' | 'error' | 'info' | 'warning';
    action?: { label: string; onClick: () => void };
    persistent?: boolean;
}

interface IToasterStore {
    toastMessages: Array<ToastMessage>;
    publishToast: (message: ToastMessage) => void;
    dismissToast?: (id: number) => void;
}

const CloseIcon = withIcon(faXmark);
const SuccessIcon = withIcon(faCircleCheck);
const WarningIcon = withIcon(faTriangleExclamation);
const ErrorIcon = withIcon(faCircleXmark);

const ToasterContext = createContext<IToasterStore>(null);

// the latest published toasts kept rendered; Radix manages which of them are still visible
const MAX_BUFFER_SIZE = 6;

// Hook-based imperative API for publishing toasts
export const useToaster = () => useContext(ToasterContext);

export const Toast = ({
    id,
    title,
    description,
    status = 'info',
    action,
    persistent,
    onDismiss,
    ...toastProps
}: Omit<React.ComponentPropsWithRef<typeof ToastPrimitive.Root>, 'title' | 'id'> &
    ToastMessage & { onDismiss?: () => void }) => {
    const [open, setOpenState] = useState(true);
    const setOpen = (value: boolean) => {
        setOpenState(value);
        if (!value) onDismiss?.();
    };

    return (
        <ToastPrimitive.Root
            className="bg-content-black/90 text-content-white backdrop-blur-md rounded-2xl py-4 px-5 flex gap-4 transition-transform duration-200 ease-out data-[state=open]:animate-slide-in data-[state=closed]:animate-hide data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:animate-swipe-out pointer-events-auto"
            open={open}
            onOpenChange={setOpen}
            duration={persistent ? Infinity : undefined}
            {...toastProps}
        >
            <div className="flex gap-4 items-center">
                {status === 'success' && <SuccessIcon className="text-content-success-weak text-lg" />}
                {status === 'warning' && <WarningIcon className="text-content-warning-weak text-lg" />}
                {status === 'error' && <ErrorIcon className="text-content-danger-weak text-lg" />}

                <div className="flex flex-col gap-2 justify-center flex-1">
                    {title && <ToastPrimitive.Title className="font-semibold text-sm">{title}</ToastPrimitive.Title>}
                    {description && (
                        <ToastPrimitive.Description className="text-sm font-medium">
                            {description}
                        </ToastPrimitive.Description>
                    )}
                    {action && (
                        <Button
                            variant="ghost"
                            className="!text-content-black !border-line-transparent !bg-surface-white hover:!bg-surface-white/90 self-start !px-3 !py-1 mt-1"
                            onClick={() => {
                                action.onClick();
                                setOpen(false);
                            }}
                        >
                            {action.label}
                        </Button>
                    )}
                </div>
            </div>
            <ToastPrimitive.Close className="toast__action" aria-label="Close" asChild>
                <button
                    className="w-8 h-8 flex-none hover:bg-surface-white/20 active:bg-surface-white/30 transition-all rounded -mt-1 -mr-2 -mb-1 opacity-50 hover:opacity-100"
                    onClick={() => setOpen(false)}
                >
                    <CloseIcon />
                </button>
            </ToastPrimitive.Close>
        </ToastPrimitive.Root>
    );
};

/**
 * Provides:
 * 1. The Radix primitive provider which injects shared configuration into all toast components
 * 2. A custom context (ToasterContext) that holds the state & API for the imperative useToaster hook
 */
export const Provider = (props: any) => {
    const [toastMessages, setToastMessages] = useState<Array<ToastMessage>>([]);
    const idRef = useRef(0);

    const publishToast = useCallback((toastMessage: ToastMessage) => {
        const id = idRef.current++;
        setToastMessages((current) => {
            const next = [...current, { ...toastMessage, id }];
            if (next.length <= MAX_BUFFER_SIZE) return next;
            const evictIndex = next.findIndex((message) => !message.persistent);
            return next.filter((_, index) => index !== (evictIndex === -1 ? 0 : evictIndex));
        });
    }, []);

    const dismissToast = useCallback((id: number) => {
        setToastMessages((current) =>
            current.some((message) => message.id === id && message.persistent)
                ? current.map((message) => (message.id === id ? { ...message, persistent: false } : message))
                : current
        );
    }, []);

    return (
        <ToasterContext.Provider value={{ toastMessages, publishToast, dismissToast }}>
            <ToastPrimitive.Provider duration={TOAST_DURATION} swipeDirection={null} {...props} />
        </ToasterContext.Provider>
    );
};

/**
 * Renderer for all toasts (declarative and imperative ones). This does two things:
 * 1. It mounts the Radix-managed viewport into the modal root so all toasts are rendered there
 * 2. It renders the toasts published imperatively via the useToaster hook as components (which will be picked up by
 *    Radix & portaled into the Radix Viewport via 1. (Note: since visibility of toasts is managed by Radix, we
 *    always render a buffer of the latest published toasts here with a unique key per toast message)
 *
 * See https://www.radix-ui.com/primitives/docs/components/toast
 */
export const Viewport = (props: any) => {
    const { className, ...passThroughProps } = props;
    const { toastMessages, dismissToast } = useToaster();

    const modalRoot = document.getElementById('modal-root');
    return (
        <>
            {toastMessages.map((message: ToastMessage) => (
                <Toast key={message.id} {...message} onDismiss={() => dismissToast?.(message.id)} />
            ))}
            {createPortal(
                <ToastPrimitive.Viewport
                    className={classnames(
                        'w-full max-w-[32rem] flex flex-col items-end gap-3 z-[70000] right-0 bottom-0 fixed p-6',
                        className
                    )}
                    {...passThroughProps}
                />,
                modalRoot
            )}
        </>
    );
};

export default {
    Toast,
    Provider,
    Viewport,
};
